// src/components/client/MealPlan.js
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Divider,
  Snackbar,
  Stack,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { canonicalI18n, pathLangFrom } from "../../utils/lang";
import { getActiveEntitlementEndDate } from "../../utils/entitlements";
import { useLocationCtx } from "../../context/LocationContext";
import { resolveCountryId } from "../../utils/country";
import { useCountryGate } from "../../hooks/useCountryGate";

import api from "../../services/api";
import { AuthContext } from "../../context/AuthContext";
import MealPlanLoader from "../common/MealPlanLoader";
import { trackEvent } from "../../utils/analytics/gtag";
import { withCountryPrefix, withQuery } from "../utils/routeHelpers";
import { persistLastShoppingEntry } from "../../utils/shoppingEntry";
import SplitMealsManageDialog from "./SplitMealsManageDialog";
import PantryInsightsDialog from "./PantryInsightsDialog";
import PantryRecommendationsDialog from "./PantryRecommendationsDialog";

/* -------------------------------- HELPERS -------------------------------- */

function formatDate(isoDateString) {
  const date = new Date(isoDateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function getDatesInRange(start, end) {
  const dateArray = [];
  let currentDate = new Date(start);
  const endD = new Date(end);
  while (currentDate <= endD) {
    dateArray.push(currentDate.toISOString().split("T")[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dateArray;
}

function apiLangFrom(lng) {
  const l = (lng || "").toLowerCase();
  const map = {
    gr: "gr",
    el: "gr",
    "el-gr": "gr",
    "el_gr": "gr",
    en: "en",
    "en-gb": "en_gb",
    "en-uk": "en_gb",
    "en-us": "en_us",
    ar: "ar",
    bg: "bg",
    de: "de",
    es: "es",
    fr: "fr",
    hr: "hr",
    it: "it",
    ro: "ro",
    ru: "ru",
    sq: "sq",
    sr: "sr",
    sv: "sv",
    ur: "ur",
    zh: "zh",
    "zh-cn": "zh",
    zh_tw: "zh",
    "zh-tw": "zh",
    "zh-hans": "zh",
    "zh-hant": "zh",
  };
  return map[l] || map[l.split("-")[0]] || "en";
}

function normalizeGRStoreLocationId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.toLowerCase().startsWith("gr:")) {
    const tail = raw.split(":", 1)[1];
    const parsed = Number(tail);
    return Number.isFinite(parsed) ? `gr:${Math.trunc(parsed)}` : raw;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? `gr:${Math.trunc(parsed)}` : raw;
}

const MEAL_TYPE_KEYS = [
  "breakfast",
  "brunch",
  "lunch",
  "afternoon",
  "dinner",
  "appetizer",
  "dessert",
];

function parseApiError(err) {
  const data =
    (err && err.response && err.response.data) ? err.response.data : null;

  const errorKey = data && data.error_key ? data.error_key : null;
  const errorParams = data && data.error_params ? data.error_params : {};
  const showHelpdesk = !!(data && data.show_helpdesk);

  const fallbackMsg =
    (data && (data.message || data.error)) ||
    (err && err.message) ||
    "";

  return { errorKey, errorParams, showHelpdesk, fallbackMsg };
}

function getRangeDays(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
}

function computeMaxAllowedDays(user) {
  if (!user) return 3;
  const entitlementEnd = getActiveEntitlementEndDate(user);
  if (entitlementEnd) return Number.POSITIVE_INFINITY;
  return 14;
}

function getCanonicalMealDisplayCost(item) {
  return Number(item?.ingredient_cost ?? 0);
}

function getCanonicalMealShoppingCost(item) {
  return Number(
    item?.shopping_cost ??
    item?.adjusted_cost ??
    item?.package_cost ??
    item?.shortfall_cost ??
    0
  );
}

function getCanonicalPlanDisplayCost(plan) {
  return Number(
    plan?.total_cost ??
    plan?.display_cost ??
    plan?.theoretical_total_cost ??
    plan?.cost_snapshot_json?.display_total ??
    plan?.cost_snapshot_json?.theoretical_total_cost ??
    plan?.cost_snapshot_json?.theoretical_total ??
    plan?.meal_plan_total_cost ??
    0
  );
}

function getCanonicalPlanTheoreticalCost(plan) {
  return Number(
    plan?.theoretical_total_cost ??
    plan?.cost_snapshot_json?.theoretical_total_cost ??
    plan?.cost_snapshot_json?.theoretical_total ??
    plan?.meal_plan_total_cost ??
    plan?.total_cost ??
    0
  );
}

function getCanonicalPlanShoppingCost(plan) {
  return Number(
    plan?.shopping_total ??
    plan?.cost_snapshot_json?.shopping_total ??
    plan?.cost_snapshot_json?.expected_shopping_total ??
    plan?.shortfall_cost ??
    0
  );
}

function normalizeMealEntryForUi(meal, { mealDate, mealTypeKey, fallbackServings }) {
  const plannedServings = Number(
    meal?.planned_servings ??
    meal?.base_servings ??
    meal?.household_members ??
    fallbackServings ??
    1
  ) || 1;
  const ingredientCost = Number(meal?.ingredient_cost ?? 0);
  const shoppingCost = Number(
    meal?.shopping_cost ??
    meal?.adjusted_cost ??
    meal?.package_cost ??
    meal?.shortfall_cost ??
    0
  );
  const theoreticalCost = Number(meal?.theoretical_cost ?? ingredientCost);

  if (
    process.env.REACT_APP_DEBUG_MEALPLAN_COSTS === "1" &&
    meal?.recipe_id &&
    !meal?.is_leftover &&
    ingredientCost <= 0
  ) {
    // Helps trace bad payloads without reintroducing cost fallbacks.
    console.warn("[MealPlan] non-leftover meal missing ingredient_cost", {
      recipe_id: meal?.recipe_id,
      recipe_name: meal?.recipe_name || meal?.name || "",
      ingredient_cost: meal?.ingredient_cost,
      display_cost: meal?.display_cost,
      theoretical_cost: meal?.theoretical_cost,
      cost_full: meal?.cost_full,
      total_cost_for_planned_batch: meal?.total_cost_for_planned_batch,
      cost: meal?.cost,
      shopping_cost: meal?.shopping_cost,
      package_cost: meal?.package_cost,
      shortfall_cost: meal?.shortfall_cost,
      meal_date: meal?.meal_date || mealDate,
      meal_type_key:
        meal?.meal_type_key ||
        meal?.meal_type ||
        meal?.meal_type_en ||
        mealTypeKey ||
        "meal",
    });
  }

  return {
    ...meal,
    meal_plan_item_id: meal?.meal_plan_item_id || meal?.meal_plan_item?.id || (meal?.recipe_id ? meal?.id : null),
    meal_date: meal?.meal_date || mealDate,
    meal_type_key:
      meal?.meal_type_key ||
      meal?.meal_type ||
      meal?.meal_type_en ||
      meal?.meal_type_el ||
      mealTypeKey ||
      "meal",
    recipe_id: meal?.recipe_id || meal?.id || null,
    recipe_name: meal?.recipe_name || meal?.name || "",
    ingredient_cost: ingredientCost,
    display_cost: ingredientCost,
    shopping_cost: shoppingCost,
    theoretical_cost: theoreticalCost,
    cost: ingredientCost,
    cost_full: theoreticalCost,
    total_cost_for_planned_batch: ingredientCost,
    cost_per_serving:
      meal?.cost_per_serving != null
        ? Number(meal.cost_per_serving)
        : ingredientCost / Math.max(plannedServings, 1),
    inventory_cost: Number(meal?.inventory_cost ?? 0),
    shortfall_cost: shoppingCost,
    package_cost: Number(meal?.package_cost ?? shoppingCost),
    planned_servings: plannedServings,
    base_servings: Number(meal?.base_servings ?? plannedServings),
    shopping_cost_per_serving:
      meal?.shopping_cost_per_serving != null
        ? Number(meal.shopping_cost_per_serving)
        : shoppingCost / Math.max(plannedServings, 1),
  };
}

function normalizeMealPlanForUi(planLike) {
  if (!planLike) return null;

  const fallbackServings = Number(
    planLike?.servings_per_cook ?? planLike?.household_members ?? 1
  ) || 1;
  const days = {};

  if (planLike?.days && typeof planLike.days === "object") {
    Object.entries(planLike.days || {}).forEach(([day, mealsByType]) => {
      const dayObj = {};
      Object.entries(mealsByType || {}).forEach(([mealTypeKey, mealGroup]) => {
        const meals = Array.isArray(mealGroup) ? mealGroup : mealGroup ? [mealGroup] : [];
        dayObj[mealTypeKey] = meals.map((meal) =>
          normalizeMealEntryForUi(meal, {
            mealDate: day,
            mealTypeKey,
            fallbackServings,
          })
        );
      });
      days[day] = dayObj;
    });
  } else if (Array.isArray(planLike?.items)) {
    (planLike.items || []).forEach((item) => {
      const day = item?.meal_date || item?.date;
      if (!day) return;

      if (Array.isArray(item?.meals)) {
        item.meals.forEach((meal) => {
          const mealTypeKey =
            meal?.meal_type_key || meal?.meal_type || meal?.meal_type_en || meal?.meal_type_el || "meal";
          if (!days[day]) days[day] = {};
          if (!days[day][mealTypeKey]) days[day][mealTypeKey] = [];
          days[day][mealTypeKey].push(
            normalizeMealEntryForUi(meal, {
              mealDate: day,
              mealTypeKey,
              fallbackServings,
            })
          );
        });
        return;
      }

      const mealTypeKey =
        item?.meal_type_key || item?.meal_type || item?.meal_type_en || item?.meal_type_el || "meal";
      if (!days[day]) days[day] = {};
      if (!days[day][mealTypeKey]) days[day][mealTypeKey] = [];
      days[day][mealTypeKey].push(
        normalizeMealEntryForUi(item, {
          mealDate: day,
          mealTypeKey,
          fallbackServings,
        })
      );
    });
  }

  const normalizedPlan = {
    ...planLike,
    days,
  };

  const displayCost = getCanonicalPlanDisplayCost(normalizedPlan);
  const theoreticalTotal = getCanonicalPlanTheoreticalCost(normalizedPlan);

  normalizedPlan.display_cost = displayCost;
  normalizedPlan.shopping_total = Number(
    normalizedPlan?.shopping_total ??
    normalizedPlan?.cost_snapshot_json?.shopping_total ??
    normalizedPlan?.cost_snapshot_json?.expected_shopping_total ??
    normalizedPlan?.shortfall_cost ??
    0
  );
  normalizedPlan.total_cost = displayCost;
  normalizedPlan.shortfall_cost = normalizedPlan.shopping_total;
  normalizedPlan.theoretical_total_cost = theoreticalTotal;

  return normalizedPlan;
}

/* ------------------------------- MAIN VIEW ------------------------------- */

export default function MealPlan() {
  const { t, i18n } = useTranslation();
  const paramsUrl = useParams();
  const urlPrefix = pathLangFrom(paramsUrl.lang || i18n.language || "en");
  const canonical = canonicalI18n(i18n.language);
  const isGreekLang = pathLangFrom(canonical) === "el";
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [loading, setLoading] = useState(false);
  const [recipeLoading, setRecipeLoading] = useState(false);

  const [profile, setProfile] = useState(null);

  const [mealPlan, setMealPlan] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [totalCost, setTotalCost] = useState(0.0);
  const [inventoryCost, setInventoryCost] = useState(0.0);
  const [remainingBudget, setRemainingBudget] = useState(0.0);
  const [costSnapshot, setCostSnapshot] = useState(null);

  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [splitMealsDialogMeal, setSplitMealsDialogMeal] = useState(null);
  const [pantryInsightsOpen, setPantryInsightsOpen] = useState(false);
  const [pantryRecommendationsOpen, setPantryRecommendationsOpen] = useState(false);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [changingMeal, setChangingMeal] = useState(null);

  const [recipeOptions, setRecipeOptions] = useState([]);
  const [recipeSearch, setRecipeSearch] = useState("");
  const [selectedNewRecipe, setSelectedNewRecipe] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef(null);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [snack, setSnack] = useState({
    open: false,
    text: "",
    severity: "info",
  });

  const [isNewPlan, setIsNewPlan] = useState(false);
  const costViewSentRef = useRef("");

  const { country: ctxCountry, storeLocationId: ctxStoreLocationId } = useLocationCtx();
  const country = resolveCountryId({ locationCountry: ctxCountry }).id;
  const currencySymbol = country === "2" || country === 2 ? "$" : "€";
  const { needsCountrySelection } = useCountryGate();
  const match = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/);
  const countryPrefix = match ? match[1] : "el";

  const [premiumOpen, setPremiumOpen] = useState(false);
  const [optimizationLimitOpen, setOptimizationLimitOpen] = useState(false);
  const [mpPrefs, setMpPrefs] = useState({
    storage_mode: "fresh_only",
    cooking_frequency: "daily",
    max_fridge_days_override: "",
    max_freezer_days_override: "",
    max_leftover_meals_per_week: "",
  });
  const premiumPlansPath = withQuery(withCountryPrefix("/plans", countryPrefix), {
    intent: "premium",
  });
  const activeStoreLocationId =
    Number(country || 1) === 1
      ? normalizeGRStoreLocationId(
        localStorage.getItem("gr_store_location_id") || ctxStoreLocationId || profile?.store_location_id || ""
      )
      : (ctxStoreLocationId || profile?.store_location_id || "");
  /* ----------------------- HELPERS: guest session/state -------------------- */

  async function ensureGuestSession() {
    try {
      await api.guest.start(); // sets cookie + localStorage gid
      return true;
    } catch {
      return false;
    }
  }

  async function hasSavedGuestPrefs() {
    return !!profile;
  }

  const summarizePlanDays = (days, currentPlan = mealPlan) => {
    let display = 0;
    let inventory = 0;
    let shopping = 0;

    Object.values(days || {}).forEach((mealsObj) => {
      orderedMealTypes(mealsObj || {}).forEach((mealType) => {
        const mealsArr = Array.isArray(mealsObj?.[mealType])
          ? mealsObj[mealType]
          : mealsObj?.[mealType]
            ? [mealsObj[mealType]]
            : [];

        mealsArr.forEach((meal) => {
          display += getCanonicalMealDisplayCost(meal);
          inventory += Number(meal?.inventory_cost ?? 0);
          shopping += getCanonicalMealShoppingCost(meal);
        });
      });
    });

    const budgetBase = Number(
      profile?.money_available ??
      (
        Number(currentPlan?.remaining_money ?? 0) +
        Number(getCanonicalPlanShoppingCost(currentPlan) || 0)
      )
    ) || 0;

    return {
      display: Number(display.toFixed(2)),
      inventory: Number(inventory.toFixed(2)),
      shopping: Number(shopping.toFixed(2)),
      remaining: Number((budgetBase - shopping).toFixed(2)),
    };
  };

  /* ----------------------- FETCH PROFILE (user/guest) ---------------------- */
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError("");
      try {
        // 1) household profile (user ή guest, backend κάνει resolve)
        const pr = await api.household.getProfile().catch(() => null);
        let hp = pr?.data?.data || null;

        // 2) login detection (labeling μόνο)
        const resUser = await api.users.getCurrentUser().catch(() => null);
        const u = resUser?.data || null;
        const isLogged =
          Boolean(u?.audience?.is_authenticated) ||
          Boolean(u?.is_authenticated) ||
          Boolean(user?.id);

        // 3) Αν δεν βρέθηκε από /household/profile και είμαστε GUEST, δοκίμασε guest state
        if (!hp && !isLogged) {
          try {
            const gs = await api.guest.getState();
            const guestHp = gs?.data?.household_profile || null;
            if (guestHp) hp = guestHp;
          } catch {
            // ignore
          }
        }

        setProfile(hp || null);

        if (process.env.NODE_ENV !== "production") {
          console.debug("[mealplan] household prefs source", {
            isLogged,
            fromHouseholdProfile: Boolean(pr?.data?.data),
            hasGuestFallback: Boolean(!pr?.data?.data && hp),
            profile: hp,
          });
        }

        if (hp) {
          setMpPrefs((prev) => ({
            ...prev,
            storage_mode: hp.storage_mode || (user ? "fridge" : "fresh_only"),
            cooking_frequency: hp.cooking_frequency || "daily",
            max_fridge_days_override: hp.max_fridge_days_override ?? "",
            max_freezer_days_override: hp.max_freezer_days_override ?? "",
            max_leftover_meals_per_week: hp.max_leftover_meals_per_week ?? "",
          }));
        }

        if (!hp) {
          setSnack({
            open: true,
            severity: isLogged ? "warning" : "info",
            text: isLogged
              ? t("no_household_profile_user_title") ||
              "Δεν βρέθηκε οικιακό προφίλ στον λογαριασμό σου."
              : t("no_household_profile_guest_title") ||
              "Ως επισκέπτης πρέπει πρώτα να αποθηκεύσεις προφίλ οικίας.",
          });
        }
      } catch (err) {
        setError(
          err?.response?.data?.error ||
          err?.message ||
          t("failed_to_fetch_household_profile") ||
          "Failed to load household profile."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, i18n.language]);

  /* -------------------------- LOAD SAVED MEAL PLAN ------------------------- */
  useEffect(() => {
    const fetchSavedMealPlan = async () => {
      if (!user) return; // guests δεν φορτώνουν saved plan
      if (!profile) return;

      try {
        const langParam = apiLangFrom(i18n.language);
        const res = await api.mealPlan.getSavedMealPlan({
          lang: langParam,
        });

        if (res?.data?.success && res?.data?.meal_plan) {
          const savedPlan = res.data.meal_plan;
          const normalizedPlan = normalizeMealPlanForUi(savedPlan);

          const groupedDays = { ...(normalizedPlan.days || {}) };

          const today = new Date().toISOString().split("T")[0];
          const planStartDate = savedPlan.start_date;
          const adjustedStartDate =
            today > planStartDate ? today : planStartDate;
          const planEndDate = savedPlan.end_date || today;
          const allDates = getDatesInRange(adjustedStartDate, planEndDate);

          allDates.forEach((d) => {
            if (!groupedDays[d]) groupedDays[d] = {};
          });

          const filteredDays = Object.fromEntries(
            Object.entries(groupedDays).filter(([day]) => allDates.includes(day))
          );

          normalizedPlan.days = filteredDays;

          setStartDate(adjustedStartDate);
          setEndDate(planEndDate);
          applyMealPlanState(normalizedPlan);

          setIsNewPlan(false);
          setMessage(t("loaded_saved_meal_plan") || "Loaded saved meal plan.");
        }
      } catch {
        // ignore
      }
    };

    fetchSavedMealPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile, i18n.language]);

  useEffect(() => {
    if (!mealPlan) return;
    const currencyCode = country === "2" || country === 2 ? "USD" : "EUR";
    const signature = `${mealPlan?.id || "na"}:${Number(totalCost || 0).toFixed(2)}:${currencyCode}:${country}:${!!user}`;
    if (costViewSentRef.current === signature) return;
    costViewSentRef.current = signature;
    trackEvent("meal_plan_cost_view", {
      total_cost: Number(Number(totalCost || 0).toFixed(2)),
      currency: currencyCode,
      country_id: Number(country || 1),
      is_guest: !user,
    });
  }, [mealPlan, totalCost, country, user]);

  function normalizeGuestMealplanToDays(guestMp) {
    return normalizeMealPlanForUi(guestMp);
  }

  /* ---------------------------- GENERATE PLAN ----------------------------- */
  const handleGenerateMealPlan = async () => {
    if (!startDate || !endDate) {
      setError(t("please_select_start_and_end_date") || "Select dates.");
      return;
    }

    const selectedDays = getRangeDays(startDate, endDate);
    if (selectedDays == null) {
      setError(t("guest_invalid_dates") || "Invalid start_date/end_date");
      return;
    }
    if (selectedDays < 0) {
      setError(t("guest_end_before_start") || "end_date must be >= start_date");
      return;
    }
    const maxAllowedDays = computeMaxAllowedDays(user);
    if (Number.isFinite(maxAllowedDays) && selectedDays > maxAllowedDays) {
      setError(
        t("guest_plan_range_not_allowed", {
          max_days: maxAllowedDays,
          defaultValue: `You can generate up to ${maxAllowedDays} days.`,
        })
      );
      return;
    }

    // STRICT GATE: guest MUST have household profile saved
    if (!user) {
      const sesOk = await ensureGuestSession();
      if (!sesOk) {
        setError(
          t("guest_session_failed") ||
          "Αποτυχία δημιουργίας guest συνεδρίας."
        );
        return;
      }

      const has = await hasSavedGuestPrefs();
      if (!has) {
        setSnack({
          open: true,
          severity: "warning",
          text:
            t("guest_profile_required_desc") ||
            "Ως επισκέπτης πρέπει πρώτα να συμπληρώσεις και να αποθηκεύσεις προφίλ οικίας.",
        });
        navigate(`/${urlPrefix}/client/household/profile`);
        return;
      }
    } else {
      // Logged-in: respect trial/subscription end
      const subExpDate = getActiveEntitlementEndDate(user);
      if (subExpDate) {
        const requestedEndDate = new Date(endDate);
        if (requestedEndDate > subExpDate) {
          setError(
            t("meal_plan_date_exceeds_subscription") ||
            `Το πλάνο μπορεί να δημιουργηθεί έως ${subExpDate.toLocaleDateString()}.`
          );
          return;
        }
      }
    }

    setLoading(true);
    setMessage("");
    setMealPlan(null);
    setError("");

    const days = Math.max(
      1,
      Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000) + 1
    );
    const householdMembers =
      Number(
        profile?.household_members ??
        profile?.cooking_servings ??
        profile?.batch_servings ??
        1
      ) || 1;
    const currencyCode = country === "2" || country === 2 ? "USD" : "EUR";
    trackEvent("meal_plan_generate", {
      is_guest: !user,
      days,
      household_members: householdMembers,
      currency: currencyCode,
      country_id: Number(country || 1),
    });

    try {
      const langParam = apiLangFrom(i18n.language);
      let res;

      if (user && profile?.id) {
        const premiumPayload = {
          storage_mode: mpPrefs.storage_mode,
          cooking_frequency: mpPrefs.cooking_frequency,
          max_fridge_days_override: mpPrefs.max_fridge_days_override ? Number(mpPrefs.max_fridge_days_override) : null,
          max_freezer_days_override: mpPrefs.max_freezer_days_override ? Number(mpPrefs.max_freezer_days_override) : null,
          max_leftover_meals_per_week: mpPrefs.max_leftover_meals_per_week ? Number(mpPrefs.max_leftover_meals_per_week) : null,
        };

        res = await api.mealPlan.generate(profile.id, {
          start_date: startDate,
          end_date: endDate,
          lang: langParam,
          country_id: Number(country || 1),
          store_location_id: activeStoreLocationId || undefined,
          debug: 1,
          ...premiumPayload,
        });

      } else {
        // Guest generate
        // ✅ Guest route
        await ensureGuestSession(); // σημαντικό, να υπάρχει cookie sid
        res = await api.guestMealPlan.generateGuest({
          start_date: startDate,
          end_date: endDate,
          lang: langParam,
          country_id: Number(country || 1),
          store_location_id: activeStoreLocationId || undefined,
          debug: 1,
        });
      }


      const data = res?.data || {};
      const ok = data.success === true || data.ok === true;
      const planRaw = data.meal_plan || data.plan || null;

      if (ok && planRaw) {
        // ✅ αν είμαστε guest και το backend δίνει items αντί για days
        const plan =
          !user && !planRaw.days
            ? normalizeGuestMealplanToDays(planRaw)
            : normalizeMealPlanForUi(planRaw);

        applyMealPlanState(plan);

        setMessage(t("meal_plan_generated_successfully") || "Meal plan generated.");
        setIsNewPlan(true);

        const hasActualRecipes = Object.values(plan.days || {}).some((mealsObj) =>
          Object.values(mealsObj || {}).some((m) =>
            Array.isArray(m) ? m.some((x) => x?.recipe_id) : Boolean(m?.recipe_id)
          )
        );

        if (!hasActualRecipes) {
          setSnack({
            open: true,
            severity: "warning",
            text:
              t("no_recipes_passed_filters") ||
              "Δεν βρέθηκαν συνταγές για τα φίλτρα/περιοχή/τιμές. Προσάρμοσε προτιμήσεις στο προφίλ.",
          });
        }
      }
      else {
        throw new Error(
          res?.data?.message ||
          t("failed_to_generate_meal_plan") ||
          "Failed to generate meal plan."
        );
      }
    } catch (err) {
      if (err?.response?.status === 429 && err?.response?.data?.code === "GUEST_LIMIT") {
        setOptimizationLimitOpen(true);
        return;
      }
      const { errorKey, errorParams, showHelpdesk, fallbackMsg } = parseApiError(err);

      // i18n message (αν υπάρχει key)
      const localized =
        errorKey
          ? t(errorKey, { ...(errorParams || {}), defaultValue: "" })
          : "";

      const finalMsg =
        localized ||
        fallbackMsg ||
        t("failed_to_generate_meal_plan") ||
        "Failed to generate meal plan.";

      setError(finalMsg);

      // Αν θες: άνοιξε helpdesk UI (Snackbar/Modal/Link)
      if (showHelpdesk) {
        setSnack({
          open: true,
          severity: "info",
          text: t("helpdesk_suggestion") || "Αν συνεχιστεί, επικοινώνησε με υποστήριξη.",
        });
      }
    } finally {
      setLoading(false);
    }

  }
  /* ------------------------------ CHANGE RECIPE --------------------------- */
  const handleChangeRecipe = (day, mealType, idx) => {
    setChangingMeal({ day, mealType, idx });
    setRecipeOptions([]);
    setRecipeSearch("");
    setSelectedNewRecipe(null);
    setChangeDialogOpen(true);
  };

  useEffect(() => {
    if (!changeDialogOpen) return;
    if (!recipeSearch || recipeSearch.length < 2) {
      setRecipeOptions([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.recipes.findByName(recipeSearch, i18n.language);
        setRecipeOptions(res?.data?.data || []);
      } catch {
        setRecipeOptions([]);
      }
      setSearchLoading(false);
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [recipeSearch, i18n.language, changeDialogOpen]);

  const handleConfirmSwap = async () => {
    if (!changingMeal || !selectedNewRecipe || !mealPlan) return;

    try {
      if (!user && needsCountrySelection) {
        if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
          console.info("[MealPlan] recomputeDraft blocked: country not selected");
        }
        return;
      }

      const newDays = { ...mealPlan.days };
      const mealsArr = Array.isArray(newDays[changingMeal.day]?.[changingMeal.mealType])
        ? [...newDays[changingMeal.day][changingMeal.mealType]]
        : [];
      const currentMeal = mealsArr[changingMeal.idx] || {};
      const plannedServings =
        Number(
          currentMeal.planned_servings ??
          currentMeal.base_servings ??
          mealPlan?.servings_per_cook ??
          profile?.cooking_servings ??
          profile?.batch_servings ??
          profile?.household_members ??
          1
        ) || 1;
      mealsArr[changingMeal.idx] = {
        ...currentMeal,
        recipe_id: selectedNewRecipe.id,
        recipe_name: selectedNewRecipe.name,
        instructions: selectedNewRecipe.instructions || "",
        ingredients: selectedNewRecipe.ingredients || [],
        planned_servings: plannedServings,
        base_servings: Number(currentMeal.base_servings ?? plannedServings),
        is_leftover: false,
        leftover_location: null,
        leftover_label_key: null,
      };
      newDays[changingMeal.day] = {
        ...(newDays[changingMeal.day] || {}),
        [changingMeal.mealType]: mealsArr,
      };
      const resp = await api.mealPlan.recomputeDraft({
        start_date: startDate,
        end_date: endDate,
        days: newDays,
        servings_per_cook:
          mealPlan?.servings_per_cook ??
          profile?.cooking_servings ??
          profile?.batch_servings ??
          profile?.household_members ??
          1,
        household_members: profile?.household_members ?? mealPlan?.household_members ?? 1,
        money_available: profile?.money_available ?? 0,
        storage_mode: mpPrefs.storage_mode,
        cooking_frequency: mpPrefs.cooking_frequency,
        max_fridge_days_override: mpPrefs.max_fridge_days_override ? Number(mpPrefs.max_fridge_days_override) : null,
        max_freezer_days_override: mpPrefs.max_freezer_days_override ? Number(mpPrefs.max_freezer_days_override) : null,
        max_leftover_meals_per_week: mpPrefs.max_leftover_meals_per_week ? Number(mpPrefs.max_leftover_meals_per_week) : null,
        country_id: Number(country || 1),
        store_location_id: activeStoreLocationId || undefined,
        us_zip: profile?.us_zip || undefined,
        lang: apiLangFrom(i18n.language),
      });
      const planRaw = resp?.data?.meal_plan || null;
      if (!resp?.data?.success || !planRaw) {
        throw new Error(resp?.data?.message || t("failed_to_generate_meal_plan") || "Failed to refresh meal plan.");
      }
      applyMealPlanState(planRaw);

      setSnack({
        open: true,
        severity: "success",
        text: t("recipe_swapped_success") || "Η συνταγή άλλαξε.",
      });
    } catch (e) {
      setSnack({
        open: true,
        severity: "error",
        text:
          e?.response?.data?.message ||
          e?.message ||
          t("recipe_swapped_partial") ||
          "Δεν έγινε πλήρης ανανέωση του πλάνου.",
      });
    } finally {
      setChangeDialogOpen(false);
      setChangingMeal(null);
      setSelectedNewRecipe(null);
      setRecipeOptions([]);
      setRecipeSearch("");
    }
  };

  /* ------------------------------- RENDER HELPERS ------------------------- */

  function orderedMealTypes(dayMeals) {
    const mealKeys = Object.keys(dayMeals || {});
    return MEAL_TYPE_KEYS.filter((key) => mealKeys.includes(key)).concat(
      mealKeys.filter((key) => !MEAL_TYPE_KEYS.includes(key))
    );
  }

  function getDisplayAmount(amount) {
    const n = Number(amount || 0);
    return isNaN(n) ? "0.00" : n.toFixed(2);
  }

  function formatUnit(unit) {
    if (country === "2" || country === 2) {
      if (unit === "g" || unit === "γραμμάρια") return "oz";
      if (unit === "kg" || unit === "κιλό") return "lb";
      if (unit === "ml" || unit === "μιλιλίτρα") return "fl oz";
      if (unit === "lt" || unit === "λίτρο") return "qt";
    }
    return unit;
  }

  function convertQty(qty, unit) {
    if (country === "2" || country === 2) {
      if (unit === "g" || unit === "γραμμάρια")
        return (qty * 0.035274).toFixed(2);
      if (unit === "kg" || unit === "κιλό")
        return (qty * 2.20462).toFixed(2);
      if (unit === "ml" || unit === "μιλιλίτρα")
        return (qty * 0.033814).toFixed(2);
      if (unit === "lt" || unit === "λίτρο")
        return (qty * 1.05669).toFixed(2);
    }
    return qty;
  }

  const fetchFullRecipe = async (recipeId) => {
    try {
      const apiLang = apiLangFrom(i18n.language);
      const res = await api.recipes.getOne(recipeId, {
        params: { lang: apiLang },
      });
      const r = res?.data?.data || res?.data?.recipe || {};

      const prefer = (obj, base) => {
        if (!obj) return "";
        if (obj[base]) return obj[base];
        const key1 = `${base}_${apiLang}`;
        const key2 = `${base}_${String(apiLang).split("-")[0]}`;
        return (
          obj[key1] ??
          obj[key2] ??
          obj[`${base}_el`] ??
          obj[`${base}_en`] ??
          ""
        );
      };

      const name = prefer(r, "name");
      const instructions = prefer(r, "instructions");
      const ingredients = prefer(r, "ingredients");

      const products = Array.isArray(r.products)
        ? r.products.map((p) => {
          const pname =
            p?.name ||
            p?.[`name_${apiLang}`] ||
            p?.[`name_${String(apiLang).split("-")[0]}`] ||
            p?.name_el ||
            p?.name_en ||
            p?.generic_name_el ||
            p?.generic_name_en ||
            "";
          return { ...p, name: pname };
        })
        : [];

      return { ...r, name, instructions, ingredients, products };
    } catch {
      return null;
    }
  };

  const applyMealPlanState = (planLike) => {
    if (!planLike) return;
    const normalizedPlan = normalizeMealPlanForUi(planLike);
    const totals = summarizePlanDays(normalizedPlan.days, normalizedPlan);
    const shoppingTotal = Number(
      getCanonicalPlanShoppingCost(normalizedPlan) || totals.shopping || 0
    );
    const displayTotal = Number(
      getCanonicalPlanDisplayCost(normalizedPlan) || totals.display || 0
    );
    setMealPlan(normalizedPlan);
    setTotalCost(Number(displayTotal.toFixed(2)));
    setInventoryCost(Number(Number(normalizedPlan.total_inventory_cost || totals.inventory || 0).toFixed(2)));
    setRemainingBudget(
      Number(
        Number(
          normalizedPlan.remaining_money ??
          totals.remaining
        ).toFixed(2)
      )
    );
    setCostSnapshot(
      normalizedPlan.cost_snapshot_json ||
      {
        display_total: displayTotal,
        theoretical_total: displayTotal,
        theoretical_total_cost: displayTotal,
        expected_shopping_total: shoppingTotal,
        shopping_total: shoppingTotal,
      }
    );
  };

  const handleGuestShoppingList = async () => {
    if (!mealPlan) return setError(t("no_meal_plan_to_save") || "No plan.");
    setLoading(true);

    try {
      // Keep web and mobile on the same grouped meal-plan shopping preview bridge.
      const res = await api.shoppingList.previewFromMealPlan({
        meal_plan_items: Object.entries(mealPlan.days || {}).flatMap(([day, mealsObj]) =>
          orderedMealTypes(mealsObj).flatMap((mealType) => {
            const mealsArr = Array.isArray(mealsObj[mealType])
              ? mealsObj[mealType]
              : mealsObj[mealType]
                ? [mealsObj[mealType]]
                : [];
            return mealsArr.map((m) => ({
              meal_date: day,
              recipe_id: m.recipe_id,
              meal_type_en: mealType,
              recipe_name: m.recipe_name,
              planned_servings: m.planned_servings || profile?.cooking_servings || profile?.household_members || 1,
            }));
          })
        ),
      });

      if (!res?.data?.success) {
        throw new Error(
          res?.data?.message ||
          t("failed_to_generate_shopping_list") ||
          "Failed."
        );
      }

      // ✅ 2) σώσε το preview προσωρινά στο guest_session.state
      // πρέπει να έχεις guest session id (cookie ή localStorage gid)
      await api.guest.start().catch(() => null); // για να είσαι σίγουρος ότι υπάρχει cookie/gid

      const previewItems = Array.isArray(res?.data?.items) ? res.data.items : [];
      const previewPayload = {
        items: previewItems,
        total_cost: 0,
        total_theoretical_cost: 0,
        country_id: profile?.location_country_id || country,
        household_members: profile?.cooking_servings || profile?.household_members || 1,
        us_zip: profile?.us_zip,
        store_location_id: profile?.store_location_id,
      };

      const tempSaveRes = await api.shoppingListPreview.saveTemp(previewPayload);
      const guestListId = tempSaveRes?.data?.guest_shopping_list_id;
      if (guestListId) {
        persistLastShoppingEntry({ listId: guestListId, isGuest: true });
      }


      // ✅ 3) πήγαινε στη λίστα ως guest
      navigate(
        guestListId
          ? `/${countryPrefix}/client/shopping/list?guest=1&list_id=${encodeURIComponent(guestListId)}`
          : `/${countryPrefix}/client/shopping/list?guest=1`
      );
    } catch (err) {
      if (err?.response?.status === 429 && err?.response?.data?.code === "GUEST_LIMIT") {
        setOptimizationLimitOpen(true);
        return;
      }
      if (err?.response?.data?.code === "GUEST_OPTIMIZATION_LIMIT") {
        setOptimizationLimitOpen(true);
        return;
      }
      setError(
        err?.response?.data?.message ||
        err.message ||
        t("failed_to_generate_shopping_list") ||
        "Failed."
      );
    } finally {
      setLoading(false);
    }
  };


  const handleSaveMealPlan = async () => {
    if (!mealPlan) {
      setError(t("no_meal_plan_to_save") || "No plan.");
      return;
    }

    if (!user) {
      setError(
        t("login_required_to_save") || "Απαιτείται σύνδεση για αποθήκευση."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const items = [];
      Object.entries(mealPlan.days || {}).forEach(([day, mealsObj]) => {
        orderedMealTypes(mealsObj).forEach((mealType) => {
          const mealsArr = Array.isArray(mealsObj[mealType])
            ? mealsObj[mealType]
            : mealsObj[mealType]
              ? [mealsObj[mealType]]
              : [];

          mealsArr.forEach((m) => {
            items.push({
              meal_date: day,
              recipe_id: m.recipe_id,
              cost: Number(m?.ingredient_cost ?? 0),
              cost_full: Number(m?.ingredient_cost ?? 0),
              shortfall_cost: Number(m?.shopping_cost ?? 0),
              meal_type_key: mealType,
              meal_type_en: mealType,
              meal_type_el: mealType,
            });
          });
        });
      });

      const tEn = i18n.getFixedT("en");
      const tEl = i18n.getFixedT("el");

      const payload = {
        meal_plan_id: mealPlan.id || null,
        start_date: startDate,
        end_date: endDate,
        items: items.map((it) => ({
          ...it,
          meal_type_en: tEn(`meal_types_labels.${it.meal_type_key}`, {
            defaultValue: it.meal_type_key,
          }),
          meal_type_el: tEl(`meal_types_labels.${it.meal_type_key}`, {
            defaultValue: it.meal_type_key,
          }),
        })),
      };

      const res = await api.mealPlan.save(payload);
      if (!res?.data?.success) {
        throw new Error(res?.data?.error || t("failed_to_save_meal_plan"));
      }

      setMessage(res.data.message || t("meal_plan_saved_successfully"));
      if (res?.data?.cost_snapshot_json) {
        setCostSnapshot(res.data.cost_snapshot_json);
      }

      const gen = await api.shoppingList.generate();
      const listId =
        gen?.data?.shopping_list_id ??
        gen?.data?.list_id ??
        gen?.data?.data?.id ??
        null;

      if (listId) {
        persistLastShoppingEntry({ listId, isGuest: false });
        navigate(`/${countryPrefix}/client/shopping/list?list_id=${encodeURIComponent(
          listId
        )}`);
      } else {
        navigate(`/${countryPrefix}/client/shopping/list`);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        t("failed_to_save_meal_plan") ||
        "Failed.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------- RENDER -------------------------------- */

  const renderMealPlan = () => {
    if (!mealPlan?.days) return null;
    return Object.keys(mealPlan.days).map((day) => (
      <Paper key={day} sx={{ mb: 2, p: 2 }}>
        <Typography variant="h6" align="center" sx={{ fontWeight: "bold", mb: 1 }}>
          {formatDate(day)}
        </Typography>

        <Grid container spacing={2}>
          {orderedMealTypes(mealPlan.days[day]).map((mealType) => {
            const mealBlock = mealPlan.days[day][mealType];
            const meals = Array.isArray(mealBlock) ? mealBlock : mealBlock ? [mealBlock] : [];

            return meals.map((meal, idx) => {
              const labelKey = `meal_types_labels.${mealType.toLowerCase()}`;
              const displayLabel = isGreekLang
                ? meal.meal_type_el || t(labelKey) || mealType
                : meal.meal_type_en || t(labelKey) || mealType;

              return (
                <Grid item xs={12} sm={6} md={4} key={`${mealType}-${idx}`}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 2,
                      backgroundColor: (theme) => theme.palette.background.paper,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      {displayLabel}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography>{meal.recipe_name || t("no_recipe_selected")}</Typography>

                      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 0.5 }}>
                        {meal.is_leftover && (
                          <Chip
                            size="small"
                            label={t("leftover_label") || "Υπόλοιπο"}
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {meal.recipe_id && (
                        <Tooltip title={t("view_recipe") || "View"}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={async () => {
                              setRecipeLoading(true);
                              const fullRecipe = await fetchFullRecipe(meal.recipe_id);
                              setRecipeLoading(false);
                              if (fullRecipe) {
                                setSelectedRecipe({
                                  ...fullRecipe,
                                  recipe_name: fullRecipe.name || meal.recipe_name,
                                });
                              } else {
                                setSelectedRecipe(meal);
                              }
                              setDialogOpen(true);
                            }}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title={t("change_recipe") || "Change"}>
                        <IconButton
                          size="small"
                          onClick={() => handleChangeRecipe(day, mealType, idx)}
                          color="secondary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {!isNewPlan && meal.meal_plan_item_id && meal.recipe_id && !meal.is_leftover ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setSplitMealsDialogMeal(meal)}
                        data-testid="meal-plan-split-meals-manage"
                        sx={{ mb: 1 }}
                      >
                        {t("smart_kitchen.split_meals.manage_cta")}
                      </Button>
                    ) : null}

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1, letterSpacing: 0.2 }}
                    >
                      {t("meal_plan.recipe_use_cost") || "Κόστος συνταγής"}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                      {getDisplayAmount(Number(meal?.ingredient_cost ?? 0))}
                      {currencySymbol}
                    </Typography>

                    {meal.cost_per_serving != null && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                        {(t("cost_per_serving") || "Κόστος/μερίδα")}: {getDisplayAmount(meal.cost_per_serving)}{currencySymbol}
                      </Typography>
                    )}

                    {getCanonicalMealShoppingCost(meal) > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        <strong>{t("meal_plan.shopping_cost") || "Κόστος αγορών"}:</strong>{" "}
                        {getDisplayAmount(getCanonicalMealShoppingCost(meal))}
                        {currencySymbol}
                      </Typography>
                    )}

                    {meal.is_leftover && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {meal.leftover_label_key
                          ? t(meal.leftover_label_key)
                          : meal.leftover_location === "freezer"
                            ? (t("meal_plan.leftovers.from_freezer") || "Leftover from freezer")
                            : (t("meal_plan.leftovers.from_fridge") || "Leftover from fridge")}
                      </Typography>
                    )}

                    {(meal.package_cost != null || meal.inventory_cost != null) && (
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
                          {meal.inventory_cost != null && meal.inventory_cost > 0 && (
                            <Tooltip title={t("inventory_used") || "Inventory used"}>
                              <Chip
                                size="small"
                                icon={<HelpOutlineRoundedIcon />}
                                label={`${getDisplayAmount(meal.inventory_cost)}${currencySymbol}`}
                              />
                            </Tooltip>
                          )}
                          {meal.package_cost != null && meal.package_cost > 0 && (
                            <Tooltip title={t("meal_plan.package_cost") || "Εκτίμηση κόστους συσκευασιών"}>
                              <Chip
                                size="small"
                                icon={<HelpOutlineRoundedIcon />}
                                variant="outlined"
                                label={`${getDisplayAmount(meal.package_cost)}${currencySymbol}`}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                  </Paper>
                </Grid>
              );
            });
          })}
        </Grid>
      </Paper>
    ));
  };

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: (theme) => theme.palette.background.paper,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {t("meal_plan") || "Meal Plan"}
        </Typography>

        <Chip
          label={
            user
              ? t("planning_as_user", { name: user.username || user.email }) ||
              `Planning as user: ${user.username || user.email}`
              : t("planning_as_guest") || "Planning as guest"
          }
          color={user ? "primary" : "default"}
          variant={user ? "filled" : "outlined"}
        />

        <Tooltip title={t("refresh") || "Ανανέωση"}>
          <IconButton onClick={() => window.location.reload()} size="small">
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* TOP banner ad — μόνο για guests */}
      {/* {!user && (
        <Box sx={{ my: 2 }}>
          <AdSenseAd slot="2080997069" />
        </Box>
      )} */}

      <Box sx={{ p: 2 }}>
        {/* Empty state όταν λείπει προφίλ */}
        {!loading && !profile && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: (theme) => `1px dashed ${theme.palette.divider}`,
              textAlign: "center",
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "#121826" : "#f8fafc"),
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {user
                ? t("no_household_profile_user_title") ||
                "Δεν βρέθηκε οικιακό προφίλ για τον λογαριασμό σου."
                : t("no_household_profile_guest_title") ||
                "Ως επισκέπτης πρέπει πρώτα να αποθηκεύσεις προφίλ οικίας."}
            </Typography>

            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
              {user
                ? t("no_household_profile_user_desc") ||
                "Ρύθμισε το οικιακό προφίλ για προσαρμοσμένο πλάνο."
                : t("no_household_profile_guest_desc") ||
                "Αποθήκευσε βασικές προτιμήσεις για να δημιουργήσεις πλάνο γευμάτων."}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={() => navigate(`/${urlPrefix}/client/household/profile`)}
              >
                {t("go_create_household_profile") || "Δημιούργησε προφίλ οικίας"}
              </Button>
            </Box>
          </Paper>
        )}

        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="h4" gutterBottom align="center">
          {t("date_range_title") || "Επιλογή ημερομηνιών"}
        </Typography>

        {/* Date pickers */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "center", gap: 2 }}>
          <TextField
            InputProps={{
              sx: {
                color: isDark ? "white" : "black",
                "& input": {
                  color: isDark ? "white" : "black",
                  "&::-webkit-calendar-picker-indicator": {
                    filter: isDark ? "invert(1)" : "none",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.3)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "#fff" : "#000",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "#fff" : "#000",
                },
              },
            }}
            label={t("start_date") || "Start Date"}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            InputProps={{
              sx: {
                color: isDark ? "white" : "black",
                "& input": {
                  color: isDark ? "white" : "black",
                  "&::-webkit-calendar-picker-indicator": {
                    filter: isDark ? "invert(1)" : "none",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.3)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "#fff" : "#000",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "#fff" : "#000",
                },
              },
            }}
            label={t("end_date") || "End Date"}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>
              {t("premium_leftovers_panel") || "Leftovers & storage"}
            </Typography>
            <Button size="small" onClick={() => setPremiumOpen((v) => !v)}>
              {premiumOpen ? (t("hide") || "Hide") : (t("customize") || "Adjust")}
            </Button>
          </Box>

          {premiumOpen && (
            <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
              {/* Storage mode */}
              <TextField
                select
                label={t("storage_mode") || "How you keep leftovers"}
                value={mpPrefs.storage_mode}
                onChange={(e) => setMpPrefs((p) => ({ ...p, storage_mode: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="fresh_only">{t("fresh_only") || "Fresh only"}</option>
                <option value="fridge">{t("fridge_leftovers") || "Fridge"}</option>
                <option value="fridge_freezer">{t("fridge_freezer_leftovers") || "Fridge + freezer"}</option>
              </TextField>

              {/* Cooking frequency */}
              <TextField
                select
                label={t("cooking_frequency") || "How often you cook"}
                value={mpPrefs.cooking_frequency}
                onChange={(e) => setMpPrefs((p) => ({ ...p, cooking_frequency: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="daily">{t("cook_daily") || "Cook every day"}</option>
                <option value="every_2_days">{t("cook_every_2_days") || "Cook every 2 days"}</option>
                <option value="3x_week">{t("cook_3x_week") || "Cook 3x/week"}</option>
                <option value="weekly_prep">{t("meal_prep_weekly") || "Meal prep weekly (batch)"}</option>
              </TextField>

              {/* Rules */}
              <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" } }}>
                <TextField
                  label={t("max_fridge_days") || "Fridge days"}
                  value={mpPrefs.max_fridge_days_override}
                  onChange={(e) => setMpPrefs((p) => ({ ...p, max_fridge_days_override: e.target.value.replace(/\D/g, "") }))}
                  placeholder="3"
                />
                <TextField
                  label={t("max_freezer_days") || "Freezer days"}
                  value={mpPrefs.max_freezer_days_override}
                  onChange={(e) => setMpPrefs((p) => ({ ...p, max_freezer_days_override: e.target.value.replace(/\D/g, "") }))}
                  placeholder="60"
                />
                <TextField
                  label={t("max_leftovers_per_week") || "Leftover meals per week"}
                  value={mpPrefs.max_leftover_meals_per_week}
                  onChange={(e) => setMpPrefs((p) => ({ ...p, max_leftover_meals_per_week: e.target.value.replace(/\D/g, "") }))}
                  placeholder="4"
                />
              </Box>

              {/* Premium preview */}
              <Alert severity="info">
                {t("premium_preview_text") || "Preview:"}{" "}
                {(() => {
                  const sd = startDate ? new Date(startDate) : null;
                  const ed = endDate ? new Date(endDate) : null;
                  if (!sd || !ed) return (t("select_dates_first") || "Select dates first");
                  const days = Math.floor((ed - sd) / (1000 * 60 * 60 * 24)) + 1;
                  const freq = mpPrefs.cooking_frequency;
                  let cooks = days;
                  if (freq === "every_2_days") cooks = Math.ceil(days / 2);
                  if (freq === "3x_week") cooks = Math.ceil((days / 7) * 3);
                  if (freq === "weekly_prep") cooks = Math.ceil(days / 7);
                  return `${t("youll_cook") || "You’ll cook"} ${cooks} ${t("times_in") || "times in"} ${days} ${t("days") || "days"}.`;
                })()}
              </Alert>
            </Box>
          )}
        </Paper>


        <Box textAlign="center" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleGenerateMealPlan}
            disabled={loading}
          >
            {user
              ? t("generate_meal_plan_as_user") || "Δημιούργησε πλάνο (χρήστης)"
              : t("generate_meal_plan_as_guest") || "Δημιούργησε πλάνο (επισκέπτης)"}
          </Button>
        </Box>

        {loading && <MealPlanLoader />}

        {mealPlan && (
          <Box sx={{ mt: 4 }}>
            {renderMealPlan()}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="h6">{t("plan_summary") || "Σύνοψη"}</Typography>
                {mealPlan?.id ? (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setPantryRecommendationsOpen(true)}
                      data-testid="meal-plan-pantry-recommendations"
                    >
                      {t("smart_kitchen.recommendations.cta")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setPantryInsightsOpen(true)}
                      data-testid="meal-plan-pantry-insights"
                    >
                      {t("smart_kitchen.pantry_intelligence.cta")}
                    </Button>
                  </Stack>
                ) : null}
              </Box>
              {costSnapshot?.expected_shopping_total != null ? (
                <Tooltip title={t("snapshot_price_info") || "Prices may have changed since this plan was created."}>
                  <Typography sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <strong>{t("meal_plan.shopping_cost") || "Estimated shopping cost"}:</strong>{" "}
                    {getDisplayAmount(costSnapshot.expected_shopping_total)}
                    {currencySymbol}
                    <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                  </Typography>
                </Tooltip>
              ) : null}
              <Typography>
                <strong>{t("meal_plan.recipe_use_cost") || "Θεωρητικό κόστος συνταγών"}:</strong>{" "}
                {getDisplayAmount(totalCost)}
                {currencySymbol}
              </Typography>
              {getCanonicalPlanShoppingCost(mealPlan) > 0 ? (
                <Typography>
                  <strong>{t("meal_plan.shopping_total") || "Προγραμματισμένο κόστος αγορών"}:</strong>{" "}
                  {getDisplayAmount(getCanonicalPlanShoppingCost(mealPlan))}
                  {currencySymbol}
                </Typography>
              ) : null}
              <Typography>
                <strong>{t("inventory_used") || "Απόθεμα"}:</strong>{" "}
                {getDisplayAmount(inventoryCost)}
                {currencySymbol}
              </Typography>
              <Typography>
                <strong>{t("remaining_budget") || "Υπόλοιπο"}:</strong>{" "}
                {getDisplayAmount(remainingBudget)}
                {currencySymbol}
              </Typography>
            </Box>

            {/* Save για logged-in */}
            {isNewPlan && user && (
              <Button
                variant="contained"
                color="secondary"
                sx={{ mt: 2 }}
                onClick={handleSaveMealPlan}
                disabled={loading}
              >
                {t("save_meal_plan") || "Αποθήκευση"}
              </Button>
            )}

            {/* Guest: δημιουργία προσωρινής λίστας */}
            {isNewPlan && !user && (
              <Button
                variant="contained"
                color="secondary"
                sx={{ mt: 2, ml: { xs: 0, sm: 2 } }}
                onClick={handleGuestShoppingList}
                disabled={loading}
              >
                {t("create_shopping_list") || "Δημιούργησε λίστα αγορών"}
              </Button>
            )}
          </Box>
        )}

        <SplitMealsManageDialog
          open={Boolean(splitMealsDialogMeal)}
          meal={splitMealsDialogMeal}
          onClose={() => setSplitMealsDialogMeal(null)}
        />

        <PantryInsightsDialog
          open={pantryInsightsOpen}
          mealPlanId={mealPlan?.id}
          onClose={() => setPantryInsightsOpen(false)}
        />

        <PantryRecommendationsDialog
          open={pantryRecommendationsOpen}
          mealPlanId={mealPlan?.id}
          onClose={() => setPantryRecommendationsOpen(false)}
        />

        {/* DIALOG: προβολή συνταγής */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>
            {selectedRecipe?.recipe_name || selectedRecipe?.name || t("view_recipe") || "Recipe"}
          </DialogTitle>
          <DialogContent dividers>
            {recipeLoading ? (
              <Box sx={{ textAlign: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <DialogContentText sx={{ mb: 2 }}>
                  <strong>{t("instructions_label") || "Instructions"}:</strong>
                  <br />
                  {selectedRecipe?.instructions || t("no_instructions_available") || "-"}
                </DialogContentText>

                <DialogContentText>
                  <strong>{t("ingredients_label") || "Ingredients"}:</strong>
                </DialogContentText>

                {selectedRecipe?.products && selectedRecipe.products.length > 0 ? (
                  <>
                    {(() => {
                      const servingsCount =
                        Number(
                          mealPlan?.servings_per_cook ??
                          profile?.cooking_servings ??
                          profile?.batch_servings ??
                          profile?.household_members ??
                          1
                        ) || 1;
                      return (
                        <>
                          <Typography sx={{ mb: 1 }}>
                            {t("ingredients_for_people", { count: servingsCount }) ||
                              `Ingredients for ${servingsCount}`}
                          </Typography>
                          <ul>
                            {selectedRecipe.products.map((prod, index) => (
                              <li key={index}>
                                {prod.name}: {convertQty(prod.quantity * servingsCount, prod.unit || "g")}{" "}
                                {formatUnit(prod.unit || "g")}
                              </li>
                            ))}
                          </ul>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <Typography variant="body2">{t("no_ingredients_available") || "-"}</Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t("close") || "Κλείσιμο"}</Button>
          </DialogActions>
        </Dialog>

        {/* DIALOG: αλλαγή συνταγής */}
        <Dialog
          open={changeDialogOpen}
          onClose={() => setChangeDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{t("select_recipe") || "Select recipe"}</DialogTitle>
          <DialogContent>
            <TextField
              label={t("search_recipes") || "Search"}
              value={recipeSearch}
              onChange={(e) => setRecipeSearch(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              autoFocus
              placeholder={t("type_to_search_recipes") || "Type to search..."}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
              }}
            />

            {searchLoading && <Typography variant="body2">{t("loading") || "Loading..."}</Typography>}

            {recipeOptions.length === 0 && !searchLoading ? (
              <Typography variant="body2">{t("no_recipes_found") || "No recipes found."}</Typography>
            ) : (
              <Box>
                {recipeOptions.map((r) => (
                  <Paper
                    key={r.id}
                    sx={{
                      my: 1,
                      p: 2,
                      cursor: "pointer",
                      bgcolor: (theme) => theme.palette.background.paper,
                      outline: selectedNewRecipe?.id === r.id ? "2px solid #1976d2" : "none",
                    }}
                    onClick={() => setSelectedNewRecipe(r)}
                  >
                    <Typography variant="subtitle1">{r.name}</Typography>
                    <Typography variant="body2">{r.description || ""}</Typography>
                  </Paper>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangeDialogOpen(false)}>{t("close") || "Close"}</Button>
            <Button
              onClick={handleConfirmSwap}
              disabled={!selectedNewRecipe}
              variant="contained"
            >
              {t("change_recipe") || "Change"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* BOTTOM banner ad — μόνο για guests */}
      {/* {!user && (
        <Box sx={{ my: 2 }}>
          <AdSenseAd slot="2080997069" />
        </Box>
      )} */}

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.text}
      />
      <Dialog
        open={optimizationLimitOpen}
        onClose={() => setOptimizationLimitOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {isGreekLang ? "Δωρεάν preview βελτιστοποίησης" : "Free optimization preview"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isGreekLang
              ? "Έφτασες το όριο επισκέπτη για ενεργά πλάνα ή λίστες. Δημιούργησε λογαριασμό για να κρατάς περισσότερα ενεργά σενάρια, να παρακολουθείς τιμές και να λαμβάνεις ειδοποιήσεις."
              : "You reached the guest limit for active plans or shopping lists. Create an account to keep more active scenarios, track prices, and unlock alerts."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptimizationLimitOpen(false)}>
            {t("close") || "Close"}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setOptimizationLimitOpen(false);
              navigate(premiumPlansPath);
            }}
          >
            {isGreekLang ? "Δες πλάνα" : "View plans"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
